import React, { useEffect, useState, useCallback, createContext, useContext } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Bug } from "lucide-react";
import { useLocalStore } from "@/hooks/use-local-store";
import { useShallow } from "zustand/shallow";

// Context for opening the debug modal from anywhere
const DebugModalContext = createContext<{ open: () => void } | null>(null);

export function useDebugModal() {
  const context = useContext(DebugModalContext);
  if (!context) {
    throw new Error("useDebugModal must be used within DebugModal");
  }
  return context;
}

// Small debug button component for error pages
export function DebugButton({ className }: { className?: string }) {
  const { open } = useDebugModal();
  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={open}
      className={`h-6 px-2 text-[10px] text-muted-foreground hover:text-foreground ${className}`}
    >
      <Bug className="w-3 h-3 mr-1" />
      Debug
    </Button>
  );
}

interface DebugInfo {
  // Manifest/Permission detection
  manifestType: "ReadItem" | "ReadWriteItem" | "Unknown";
  permissionLevel: string;

  // Office context
  hostName: string;
  hostVersion: string;
  platform: string;
  owaView: string | null;
  mailboxVersion: string;

  // Environment
  userAgent: string;
  language: string;
  timestamp: string;

  // App state
  currentPage: string;
  userEmail: string | null;
  organization: string | null;

  // Requirements
  mailboxRequirementSet: string;
  isDialogApiSupported: boolean;
  isCategoriesSupported: boolean;
}

async function detectManifestType(): Promise<"ReadItem" | "ReadWriteItem" | "Unknown"> {
  try {
    // Try to check if categories API is available (requires ReadWriteItem)
    // The categories property exists only with ReadWriteItem permission
    if (Office.context.mailbox?.item) {
      const item = Office.context.mailbox.item;
      // If categories exists and we can attempt to get them, we have ReadWriteItem
      if ("categories" in item && item.categories) {
        return "ReadWriteItem";
      }
    }
    return "ReadItem";
  } catch {
    return "Unknown";
  }
}

function getOfficeDebugInfo(): Partial<DebugInfo> {
  try {
    const diagnostics = Office.context.diagnostics;
    const mailbox = Office.context.mailbox;

    // Check mailbox requirement set version
    let mailboxRequirementSet = "Unknown";
    for (const version of ["1.14", "1.13", "1.12", "1.11", "1.10", "1.9", "1.8", "1.7", "1.6", "1.5"]) {
      if (Office.context.requirements.isSetSupported("Mailbox", version)) {
        mailboxRequirementSet = version;
        break;
      }
    }

    return {
      hostName: diagnostics?.host?.toString() || "Unknown",
      hostVersion: diagnostics?.version || "Unknown",
      platform: diagnostics?.platform?.toString() || "Unknown",
      owaView: mailbox?.diagnostics?.OWAView || null,
      mailboxVersion: mailbox?.diagnostics?.hostVersion || "Unknown",
      permissionLevel: Office.context.mailbox?.item ? "Item access available" : "No item access",
      mailboxRequirementSet,
      isDialogApiSupported: Office.context.requirements.isSetSupported("DialogApi", "1.1"),
      isCategoriesSupported: Office.context.requirements.isSetSupported("Mailbox", "1.8"),
    };
  } catch (error) {
    console.error("Error getting Office debug info:", error);
    return {
      hostName: "Error",
      hostVersion: "Error",
      platform: "Error",
    };
  }
}

function DebugInfoRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex justify-between items-start py-1.5 border-b border-border last:border-b-0">
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      <span className="text-xs text-right max-w-[60%] break-all">{value}</span>
    </div>
  );
}

function DebugSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mb-4">
      <h3 className="text-sm font-semibold mb-2 text-foreground">{title}</h3>
      <div className="bg-muted/50 rounded-md p-2">{children}</div>
    </div>
  );
}

export function DebugModal({ children }: { children?: React.ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [debugInfo, setDebugInfo] = useState<DebugInfo | null>(null);

  const { page, userProfile } = useLocalStore(
    useShallow(({ page, userProfile }) => ({
      page,
      userProfile,
    })),
  );

  const openModal = useCallback(() => setIsOpen(true), []);

  const gatherDebugInfo = useCallback(async () => {
    const officeInfo = getOfficeDebugInfo();
    const manifestType = await detectManifestType();

    const info: DebugInfo = {
      manifestType,
      permissionLevel: officeInfo.permissionLevel || "Unknown",
      hostName: officeInfo.hostName || "Unknown",
      hostVersion: officeInfo.hostVersion || "Unknown",
      platform: officeInfo.platform || "Unknown",
      owaView: officeInfo.owaView || null,
      mailboxVersion: officeInfo.mailboxVersion || "Unknown",
      userAgent: navigator.userAgent,
      language: navigator.language,
      timestamp: new Date().toISOString(),
      currentPage: page,
      userEmail: userProfile?.email || null,
      organization: userProfile?.organization || null,
      mailboxRequirementSet: officeInfo.mailboxRequirementSet || "Unknown",
      isDialogApiSupported: officeInfo.isDialogApiSupported ?? false,
      isCategoriesSupported: officeInfo.isCategoriesSupported ?? false,
    };

    setDebugInfo(info);
  }, [page, userProfile]);

  // Keyboard shortcut listener (CMD/Ctrl + Shift + D)
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const isMac = navigator.platform.toUpperCase().indexOf("MAC") >= 0;
      const modifierKey = isMac ? event.metaKey : event.ctrlKey;

      if (modifierKey && event.shiftKey && event.key.toLowerCase() === "d") {
        event.preventDefault();
        setIsOpen((prev) => !prev);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  // Gather debug info when modal opens
  useEffect(() => {
    if (isOpen) {
      gatherDebugInfo();
    }
  }, [isOpen, gatherDebugInfo]);

  return (
    <DebugModalContext.Provider value={{ open: openModal }}>
      {children}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent onClose={() => setIsOpen(false)} className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              Debug Info
              <Badge variant="outline" className="text-[10px] font-normal">
                {debugInfo?.manifestType || "..."}
              </Badge>
            </DialogTitle>
            <DialogDescription>
              Press <kbd className="px-1.5 py-0.5 bg-muted rounded text-[10px] font-mono">⌘+Shift+D</kbd> to toggle
            </DialogDescription>
          </DialogHeader>

          {debugInfo && (
            <div className="mt-4 space-y-2">
              <DebugSection title="Manifest & Permissions">
                <DebugInfoRow
                  label="Manifest Type"
                  value={
                    <Badge variant={debugInfo.manifestType === "ReadWriteItem" ? "default" : "secondary"}>
                      {debugInfo.manifestType}
                    </Badge>
                  }
                />
                <DebugInfoRow
                  label="Categories API"
                  value={debugInfo.isCategoriesSupported ? "✓ Supported" : "✗ Not supported"}
                />
                <DebugInfoRow
                  label="Dialog API"
                  value={debugInfo.isDialogApiSupported ? "✓ Supported" : "✗ Not supported"}
                />
                <DebugInfoRow label="Mailbox API Version" value={debugInfo.mailboxRequirementSet} />
              </DebugSection>

              <DebugSection title="Office Context">
                <DebugInfoRow label="Host" value={debugInfo.hostName} />
                <DebugInfoRow label="Host Version" value={debugInfo.hostVersion} />
                <DebugInfoRow label="Platform" value={debugInfo.platform} />
                {debugInfo.owaView && <DebugInfoRow label="OWA View" value={debugInfo.owaView} />}
                <DebugInfoRow label="Mailbox Version" value={debugInfo.mailboxVersion} />
              </DebugSection>

              <DebugSection title="App State">
                <DebugInfoRow label="Current Page" value={debugInfo.currentPage} />
                <DebugInfoRow label="User Email" value={debugInfo.userEmail || "Not logged in"} />
                <DebugInfoRow label="Organization" value={debugInfo.organization || "N/A"} />
              </DebugSection>

              <DebugSection title="Environment">
                <DebugInfoRow label="Language" value={debugInfo.language} />
                <DebugInfoRow label="Timestamp" value={new Date(debugInfo.timestamp).toLocaleString()} />
                <DebugInfoRow
                  label="User Agent"
                  value={<span className="text-[10px] leading-tight">{debugInfo.userAgent}</span>}
                />
              </DebugSection>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </DebugModalContext.Provider>
  );
}
